// PlayFirst unified app navigation — bottom tab bar (mobile) + side nav (desktop).
// Items adapt by role (therapist / parent / super-admin).
import {
  House,
  CalendarDays,
  Library,
  Menu as MenuIcon,
  Heart,
  Bell,
  BookOpen,
} from 'lucide-react';

// Therapist primary tabs (bottom bar on mobile, top of sidebar on desktop)
export const THERAPIST_PRIMARY_TABS = [
  { id: 'home', label: 'Home', icon: House, path: '/' },
  { id: 'schedule', label: 'Schedule', icon: CalendarDays, path: '/admin/daysheet' },
  { id: 'library', label: 'Library', icon: Library, path: '/activities' },
  { id: 'menu', label: 'Menu', icon: MenuIcon, action: 'open_drawer' },
];

// Parent primary tabs — simpler, hides clinical items
export const PARENT_PRIMARY_TABS = [
  { id: 'home', label: 'Home', icon: Heart, path: '/' },
  { id: 'resources', label: 'Resources', icon: BookOpen, path: '/resources' },
  { id: 'notifications', label: 'Notifications', icon: Bell, path: '/parent/notifications' },
  { id: 'menu', label: 'Menu', icon: MenuIcon, action: 'open_drawer' },
];

// Drawer menu groups for therapists. Items inside open the drawer's sub-pages.
// `hasParentAppAccess` flips on the "My PlayFirst Parent" item — set when a
// Clinical therapist has also paid for PFP (dual-role).
export const THERAPIST_MENU_GROUPS = (isSuperAdmin, hasParentAppAccess = false) => [
  {
    label: 'Clinical',
    items: [
      { label: 'Daysheet & Timetable', path: '/admin/daysheet', icon: 'CalendarDays' },
      { label: 'Recurring timetable', path: '/timetable', icon: 'CalendarRange' },
      { label: 'Diary & reminders', path: '/diary', icon: 'ListChecks' },
      { label: 'Activity Library', path: '/activities', icon: 'Library' },
      { label: 'Resource Library', path: '/resources', icon: 'BookOpen' },
      { label: 'Invoicing', path: '/invoicing', icon: 'Receipt' },
    ],
  },
  ...(hasParentAppAccess
    ? [{
        label: 'PlayFirst Parent',
        items: [
          { label: 'My PlayFirst Parent', path: '/parent-app/home', icon: 'Heart' },
          { label: 'Toys & interests', path: '/parent-app/toys', icon: 'Package' },
          { label: "My child's Language Map", path: '/parent-app/language-map', icon: 'BookOpen' },
        ],
      }]
    : []),
  {
    label: 'Workspace',
    items: [
      { label: 'Settings & Practice Header', path: '/settings', icon: 'Settings' },
      { label: 'WhatsApp Assistant', path: '/settings#whatsapp', icon: 'MessageCircle' },
      { label: 'Try PlayFirst Lite', path: '/us', icon: 'Sparkles', external: true },
    ],
  },
  {
    label: 'Information',
    items: [
      { label: 'Continued Education', path: '/continued-education', icon: 'GraduationCap' },
      { label: 'Privacy Policy', path: '/privacy', icon: 'ShieldCheck' },
      { label: 'Terms of Service', path: '/terms', icon: 'FileText' },
      { label: 'HIPAA Information', path: '/hipaa', icon: 'Lock' },
    ],
  },
  ...(isSuperAdmin
    ? [{
        label: 'Admin',
        items: [
          { label: 'User Directory', path: '/admin/analytics', icon: 'Users' },
          { label: 'Parent Members', path: '/admin/parent-members', icon: 'Heart' },
          { label: 'Schools & PFP Seats', path: '/admin/practices', icon: 'School' },
          { label: 'Billing Lookup', path: '/admin/billing-lookup', icon: 'Search' },
          { label: 'Parent Data Lookup', path: '/admin/parent-lookup', icon: 'Search' },
          { label: 'PFP Analytics', path: '/admin/pfp-analytics', icon: 'BarChart3' },
          { label: 'PFP · Pending Invites', path: '/admin/pfp-pending-invites', icon: 'MailWarning' },
          { label: 'Partner Codes', path: '/admin/partner-codes', icon: 'Tag' },
          { label: 'Team To-do List', path: '/admin/todos', icon: 'ListTodo' },
          { label: 'Parent App — Collaborators', path: '/parent-app/collaborators', icon: 'Users2' },
          { label: 'Parent App — Home (preview)', path: '/parent-app/home', icon: 'Sparkles' },
          { label: 'Lite Stats', path: '/admin/lite', icon: 'BarChart3' },
        ],
      }]
    : []),
];

export const PARENT_MENU_GROUPS = () => [
  {
    label: 'For you',
    items: [
      { label: 'Resources shared with me', path: '/resources', icon: 'BookOpen' },
      { label: 'Notifications', path: '/parent/notifications', icon: 'Bell' },
      { label: 'Send feedback to therapist', path: '/parent/feedback', icon: 'MessageCircle' },
    ],
  },
  {
    label: 'Information',
    items: [
      { label: 'Privacy Policy', path: '/privacy', icon: 'ShieldCheck' },
      { label: 'Terms of Service', path: '/terms', icon: 'FileText' },
    ],
  },
];
